import { Button, Col, Row } from "reactstrap";
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import UploadModal from "./uploadPopup";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from 'framer-motion';
import RotatePhonePopup from "../RotatePhonePopup";
import { useSnackbar } from "../context/SnackbarContext";

// img
import avatarupload from "../../img/avatarupload.svg"
import camera from "../../img/uploadcamera.svg"
import annoycamera from "../../img/annoycamera.svg"
import galleryupload from "../../img/uploadgallery.svg"
import hintuser from "../../img/hintuser.svg"

import avatar1 from "../../img/avatar1.png"
import avatar2 from "../../img/avatar2.png"
import avatar3 from "../../img/avatar3.png"
import avatar4 from "../../img/avatar4.png"
import avatar5 from "../../img/avatar5.png"
import avatar6 from "../../img/avatar6.png"
import avatar7 from "../../img/avatar7.png"
import placeholder from "../../img/placeholder.png"
import { useLanguage } from "../context/Languagecontext";

// Default avatar image URL
const DEFAULT_AVATARS = [
    avatar1,
    avatar2,
    avatar3,
    avatar4,
    avatar5,
    avatar6,
    avatar7,
];

const DEFAULT_AVATARS2 = [
    "https://lol-image-bucket.s3.amazonaws.com/images/Avatar/Avatar-ccd6af8c-cc2f-4910-824d-744c3fd248eb.png",
    "https://lol-image-bucket.s3.amazonaws.com/images/Avatar/Avatar-c2e51864-e332-4be9-a803-90f643ccd7ca.png",
    "https://lol-image-bucket.s3.amazonaws.com/images/Avatar/Avatar-a456ddbf-c0fd-4b6f-a585-22325e9e93a2.png",
    "https://lol-image-bucket.s3.amazonaws.com/images/Avatar/Avatar-0f0e5f82-af4e-4a52-9f33-4818a8ab21c8.png",
    "https://lol-image-bucket.s3.amazonaws.com/images/Avatar/Avatar-d9278bc7-acaf-40cc-8562-a1b666a713ab.png",
    "https://lol-image-bucket.s3.amazonaws.com/images/Avatar/Avatar-d89e6e8a-fb64-4443-bf1a-465039f28e70.png"
];

// ===================== Cache Management (module scope) =====================
// Moved outside the component since these don't depend on any props/state,
// which also resolves the "missing dependency" eslint warnings for the
// effects/callbacks that use them.
const CACHE_NAME = 'lol-image-cache-v1';
const CACHE_EXPIRY_KEY = 'image-cache-expiry';
const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

// Images to cache
const IMAGES_TO_CACHE = [
    camera,
    galleryupload,
    avatarupload,
    annoycamera,
    hintuser,
    ...DEFAULT_AVATARS
];

const cacheImage = async (url, blob) => {
    try {
        const cache = await caches.open(CACHE_NAME);
        const response = new Response(blob);
        await cache.put(url, response);

        const expiry = Date.now() + CACHE_DURATION;
        localStorage.setItem(`${CACHE_EXPIRY_KEY}-${url}`, expiry.toString());

        console.log('Image cached successfully:', url);
    } catch (error) {
        console.error('Error caching image:', error);
    }
};

const getCachedImage = async (url) => {
    try {
        const expiryTime = localStorage.getItem(`${CACHE_EXPIRY_KEY}-${url}`);
        if (expiryTime && Date.now() > parseInt(expiryTime)) {
            console.log('Cache expired for:', url);
            return null;
        }

        const cache = await caches.open(CACHE_NAME);
        const response = await cache.match(url);

        if (response) {
            const blob = await response.blob();
            return URL.createObjectURL(blob);
        }
        return null;
    } catch (error) {
        console.error('Error retrieving cached image:', error);
        return null;
    }
};

const Category3 = ({ hint, uploadedImage, originalData, username, category }) => {
    const { t } = useLanguage();
    const [showModal, setShowModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState(() => {
        // Check if ShowCard updated the avatar
        const showCardState = (() => {
            try { return JSON.parse(sessionStorage.getItem('showCardState')); } catch { return null; }
        })();
        if (showCardState?.avatar) return showCardState.avatar;
        return sessionStorage.getItem('cat3SelectedImage') || null;
    });
    const [isAvatarImage, setIsAvatarImage] = useState(() => {
        const showCardState = (() => {
            try { return JSON.parse(sessionStorage.getItem('showCardState')); } catch { return null; }
        })();
        if (showCardState?.avatar) return true;
        return sessionStorage.getItem('cat3IsAvatarImage') === 'true';
    });
    const [loading, setLoading] = useState(false);
    const [imageUrls, setImageUrls] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isCheckingConnection, setIsCheckingConnection] = useState(false);
    const [isCheckingConnection2, setIsCheckingConnection2] = useState(false);
    const [isCheckingConnectionNext, setIsCheckingConnectionNext] = useState(false);
    const [imageModal, setImageModal] = useState(false);
    const [isAnyActionInProgress, setIsAnyActionInProgress] = useState(false);

    // Pagination states
    const [hasMore, setHasMore] = useState(false);
    const AVATARS_PER_PAGE = 15;

    // Image loading states
    const [imageLoadingStates, setImageLoadingStates] = useState({});

    const iosGalleryInputRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const loadingRef = useRef(false);
    const loadingMoreRef = useRef(false);
    const currentPageRef = useRef(1);
    const imageUrlsRef = useRef([]);
    const { showSnackbar } = useSnackbar();
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isSmallScreen = window.innerWidth < 380 && window.innerWidth !== 360;

    const navigate = useNavigate();

    // Cached images state
    const [cachedImages, setCachedImages] = useState({
        camera: camera,
        gallery: galleryupload,
        avatar: avatarupload,
        annoycamera: annoycamera,
        hintuser: hintuser,
    });

    // Keep a ref of the latest imageUrls so the cleanup in the initial-load
    // effect always has the current value without needing imageUrls as a dep
    // (which would otherwise re-trigger the fetch effect on every page load).
    useEffect(() => {
        imageUrlsRef.current = imageUrls;
    }, [imageUrls]);

    // Preload and cache all images
    useEffect(() => {
        const preloadAndCacheImages = async () => {
            for (const src of IMAGES_TO_CACHE) {
                try {
                    const cachedUrl = await getCachedImage(src);
                    if (cachedUrl) {
                        console.log('Image already cached:', src);
                        continue;
                    }

                    const response = await fetch(src);
                    const blob = await response.blob();
                    await cacheImage(src, blob);

                    const img = new Image();
                    img.src = src;
                } catch (error) {
                    console.error('Error preloading/caching image:', src, error);
                }
            }
        };

        preloadAndCacheImages();
    }, []);

    // Load cached images on mount
    useEffect(() => {
        const loadCachedImages = async () => {
            const [cachedCamera, cachedGallery, cachedAvatar, cachedAnnoyCamera, cachedHintUser] = await Promise.all([
                getCachedImage(camera),
                getCachedImage(galleryupload),
                getCachedImage(avatarupload),
                getCachedImage(annoycamera),
                getCachedImage(hintuser),
            ]);

            setCachedImages(prev => ({
                ...prev,
                ...(cachedCamera ? { camera: cachedCamera } : {}),
                ...(cachedGallery ? { gallery: cachedGallery } : {}),
                ...(cachedAvatar ? { avatar: cachedAvatar } : {}),
                ...(cachedAnnoyCamera ? { annoycamera: cachedAnnoyCamera } : {}),
                ...(cachedHintUser ? { hintuser: cachedHintUser } : {}),
            }));
        };

        loadCachedImages();
    }, []);

    useEffect(() => {
        setIsAnyActionInProgress(
            isCheckingConnection ||
            isCheckingConnection2 ||
            isCheckingConnectionNext ||
            isProcessing
        );
    }, [isCheckingConnection, isCheckingConnection2, isCheckingConnectionNext, isProcessing]);

    useEffect(() => {
        const isDisabled = isCheckingConnection || isCheckingConnection2 || isCheckingConnectionNext || isProcessing;
        window.dispatchEvent(new CustomEvent('dropdownDisabledState', {
            detail: { disabled: isDisabled }
        }));
    }, [isCheckingConnection, isCheckingConnection2, isCheckingConnectionNext, isProcessing]);

    // ============================ internet ==============================
    const checkInternetConnection = async () => {
        // Add 2 second delay
        await new Promise(resolve => setTimeout(resolve, 700));
        // First check navigator.onLine
        if (!navigator.onLine) {
            return false;
        }
        // Double-check with a real network request
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

            clearTimeout(timeoutId);
            return true;
        } catch (error) {
            console.log('Network check failed:', error);
            return false;
        }
    };

    const handleBoxClick = async () => {
        if (isIOS) {
            setIsCheckingConnection(true);

            // Check internet connection (includes 2 sec delay inside)
            const isOnline = await checkInternetConnection();

            setIsCheckingConnection(false);

            if (!isOnline) {
                showSnackbar(t?.common?.nointertoast || "Please turn on internet connection", "error");
                return;
            }

            if (iosGalleryInputRef.current) {
                iosGalleryInputRef.current.click();
            }
            return;
        }

        setIsCheckingConnection(true);

        // Check internet connection (includes 2 sec delay inside)
        const isOnline = await checkInternetConnection();

        setIsCheckingConnection(false);

        if (!isOnline) {
            showSnackbar(t?.common?.nointertoast || "Please turn on internet connection", "error");
            return;
        }

        setShowModal(true);
    };

    const handleBoxClick2 = async () => {
        if (isIOS) {
            setIsCheckingConnection2(true);

            // Check internet connection (includes 2 sec delay inside)
            const isOnline = await checkInternetConnection();

            setIsCheckingConnection2(false);

            if (!isOnline) {
                showSnackbar(t?.common?.nointertoast || "Please turn on internet connection", "error");
                return;
            }

            if (iosGalleryInputRef.current) {
                iosGalleryInputRef.current.click();
            }
            return;
        }

        setIsCheckingConnection2(true);

        // Check internet connection (includes 2 sec delay inside)
        const isOnline = await checkInternetConnection();

        setIsCheckingConnection2(false);

        if (!isOnline) {
            showSnackbar(t?.common?.nointertoast || "Please turn on internet connection", "error");
            return;
        }

        setShowModal(true);
    };


    // Compress image function (same as UploadModal)
    const compressImage = (file) => {
        return new Promise((resolve, reject) => {
            if (file.type === 'image/gif') {
                resolve(file);
                return;
            }

            const reader = new FileReader();
            reader.onload = (event) => {
                const img = document.createElement('img');
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;
                    const MAX_WIDTH = 1200;
                    const MAX_HEIGHT = 1200;

                    if (width > height) {
                        if (width > MAX_WIDTH) {
                            height = Math.round((height * MAX_WIDTH) / width);
                            width = MAX_WIDTH;
                        }
                    } else {
                        if (height > MAX_HEIGHT) {
                            width = Math.round((width * MAX_HEIGHT) / height);
                            height = MAX_HEIGHT;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);
                    const quality = 0.7;

                    canvas.toBlob(
                        (blob) => {
                            if (!blob) {
                                reject(new Error('Canvas to Blob conversion failed'));
                                return;
                            }
                            const compressedFile = new File([blob], file.name, {
                                type: file.type,
                                lastModified: Date.now(),
                            });
                            resolve(compressedFile);
                        },
                        file.type,
                        quality
                    );
                };
                img.onerror = () => reject(new Error('Failed to load image'));
                img.src = event.target.result;
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsDataURL(file);
        });
    };

    // Handle iOS file selection with face detection
    const handleIOSFileChange = async (e) => {
        const isOnline = await checkInternetConnection();
        if (!isOnline) {
            showSnackbar(t?.common?.nointertoast || "Please turn on internet connection", "error");
            return;
        }

        const selectedFile = e.target.files[0];

        // Reset input value to allow selecting the same file again
        e.target.value = '';

        if (!selectedFile) {
            console.log('No file selected');
            setIsProcessing(false);
            return;
        }

        console.log('File selected:', selectedFile.name, 'from iOS gallery');

        try {
            // Start processing
            setIsProcessing(true);

            // Compress image
            const compressed = await compressImage(selectedFile);

            // Check size
            if (compressed.size > 5 * 1024 * 1024) {
                setIsProcessing(false);
                showSnackbar(t?.common?.nointertoast || "Please turn on internet connection", "error");
                return;
            }

            // Convert to base64
            const reader = new FileReader();
            reader.onloadend = async () => {
                const imageDataUrl = reader.result;

                // SIMPLIFIED: Just set the image directly without face detection
                setIsProcessing(false);
                setSelectedImage(imageDataUrl);
            };
            reader.onerror = () => {
                setIsProcessing(false);
                console.error('Error reading file');
            };
            reader.readAsDataURL(compressed);
        } catch (error) {
            setIsProcessing(false);
            console.error('Error processing image:', error);
        }
    };

    const handleImageSelect = (imageData) => {
        setSelectedImage(imageData);
        setIsAvatarImage(false);
    };

    const handleAvatarClick = (avatarUrl) => {
        setSelectedImage(avatarUrl);
        setIsAvatarImage(true);
    };

    const isNextButtonEnabled = () => {
        return selectedImage;
    };

    // Handle image load
    const handleImageLoad = (url) => {
        setImageLoadingStates(prev => ({
            ...prev,
            [url]: true
        }));
    };

    // IMPROVED: Fixed getImages with pagination
    const getImages = useCallback(async (page = 1, append = false) => {
        if (loadingRef.current || loadingMoreRef.current) return; // ← uses ref, never stale

        if (append) {
            loadingMoreRef.current = true;
        } else {
            loadingRef.current = true;
            setLoading(true);
        }

        // Set a maximum timeout for the entire operation
        const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Timeout')), 8000)
        );

        try {
            const fetchPromise = axios.post(`https://api.lolcards.link/api/avatar/read`,
                {
                    page: page,
                    limit: AVATARS_PER_PAGE
                },
                {
                    headers: {
                        'Cache-Control': 'no-cache, no-store, must-revalidate',
                        'Pragma': 'no-cache',
                        'Expires': '0'
                    },
                    timeout: 6000 // 6 second timeout for axios
                }
            );

            const { data } = await Promise.race([fetchPromise, timeoutPromise]);

            let images = data?.data || [];
            // const total = data?.totalPages || 1;
            const hasMoreData = data?.hasMore || false;
            setHasMore(hasMoreData);

            // If no images from API, use defaults immediately
            if (!images || images.length === 0) {
                throw new Error('No images from API');
            }

            // Initialize loading states for new images
            const newLoadingStates = {};
            images.forEach(url => {
                newLoadingStates[url] = false;
            });
            setImageLoadingStates(prev => ({ ...prev, ...newLoadingStates }));

            // Convert API images to cached blob URLs with timeout per image
            // const cachedApiImages = await Promise.all(
            //     images.map(async (url) => {
            //         try {
            //             const cached = await getCachedImage(url);
            //             if (cached) return cached;

            //             // Add timeout for individual image fetch
            //             const controller = new AbortController();
            //             const timeoutId = setTimeout(() => controller.abort(), 3000);

            //             const response = await fetch(url, { signal: controller.signal });
            //             clearTimeout(timeoutId);

            //             const blob = await response.blob();
            //             await cacheImage(url, blob);
            //             return URL.createObjectURL(blob);
            //         } catch (error) {
            //             console.error('Error caching API image:', error);
            //             return url; // Return original URL as fallback
            //         }
            //     })
            // );

            if (append) {
                setImageUrls(prev => [...prev, ...images]);
            } else {
                setImageUrls(images);
            }
        } catch (error) {
            console.log("Failed to fetch avatars:", error);

            if (!append) {
                const shuffledDefaults = DEFAULT_AVATARS2.sort(() => 0.5 - Math.random());
                const defaultImages = shuffledDefaults.slice(0, 6);

                // Initialize loading states for default images
                const newLoadingStates = {};
                defaultImages.forEach(url => {
                    newLoadingStates[url] = false;
                });
                setImageLoadingStates(newLoadingStates);

                // Convert default avatars to cached blob URLs with faster timeout
                const cachedDefaults = await Promise.all(
                    defaultImages.map(async (url) => {
                        try {
                            const cached = await getCachedImage(url);
                            if (cached) return cached;

                            // Shorter timeout for default images (they should be cached)
                            const controller = new AbortController();
                            const timeoutId = setTimeout(() => controller.abort(), 2000);

                            const response = await fetch(url, { signal: controller.signal });
                            clearTimeout(timeoutId);

                            const blob = await response.blob();
                            await cacheImage(url, blob);
                            return URL.createObjectURL(blob);
                        } catch (error) {
                            console.error('Error caching default image:', error);
                            return url; // Return original URL as fallback
                        }
                    })
                );

                setImageUrls(cachedDefaults);
            }
        } finally {
            loadingRef.current = false;
            loadingMoreRef.current = false;
            setLoading(false);
        }
    }, []);

    // Handle scroll for pagination
    const handleScroll = useCallback(() => {
        if (!scrollContainerRef.current || loadingMoreRef.current || !hasMore) return;

        const container = scrollContainerRef.current;
        const { scrollTop, scrollHeight, clientHeight } = container;

        if (scrollTop + clientHeight >= scrollHeight * 0.8) {
            currentPageRef.current += 1;
            getImages(currentPageRef.current, true);
        }
    }, [hasMore, getImages]);

    useEffect(() => {
        getImages(1, false);

        // Cleanup blob URLs when component unmounts
        return () => {
            imageUrlsRef.current.forEach(url => {
                if (url.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
        };
    }, [getImages]);

    // Add scroll listener
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    const handleNextClick = async () => {
        setIsCheckingConnectionNext(true);
        const isOnline = await checkInternetConnection();
        setIsCheckingConnectionNext(false);

        if (!isOnline) {
            showSnackbar(t?.common?.nointertoast || "Please turn on internet connection", "error");
            return;
        }

        // Save selected image to sessionStorage
        sessionStorage.setItem('cat3SelectedImage', selectedImage);
        sessionStorage.setItem('cat3IsAvatarImage', isAvatarImage ? 'true' : 'false');

        window.scrollTo(0, 0);
        const formData = {
            originalData: originalData,
            hint: hint,
            uploadedImage: uploadedImage,
            avatar: selectedImage,
        };
        navigate(`/${username || ''}/${category || ''}/funcards`, { state: formData });
    };

    return (
        <div className=' gradient-bg GTRegular' style={{ height: '100dvh' }}>
            {isIOS && (
                <input
                    ref={iosGalleryInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleIOSFileChange}
                    style={{ display: 'none' }}
                />
            )}
            <div className='h-100 hint-bg3 pt-42'>
                <Row className='m-0 h-100'>
                    <Col className="p-0 d-flex flex-column justify-content-between" style={{ height: '100%' }}>

                        <div
                            className="d-flex justify-content-center align-items-center"
                            style={{
                                height: '12%',
                                padding: '20px 0'
                            }}
                        >
                            <div className="text-center" style={{ maxWidth: "90vw" }}>
                                <p className={`lh-sm mx-auto px-3 m-0 text-white GTBold`}
                                    style={{
                                        fontSize:
                                            t?.annoyCard?.title.length > 60
                                                ? "clamp(14px, 2vw, 17px)"
                                                : t?.annoyCard?.title.length > 40
                                                    ? "clamp(17px, 2.2vw, 18px)"
                                                    : "clamp(17px, 2.5vw, 19px)",
                                    }}
                                >
                                    {t?.annoyCard?.title || "Pick a funny face for the friend who sent this"}
                                </p>
                            </div>
                        </div>

                        {/* ============ */}
                        <div
                            className="overflow-hidden position-relative rounded-circle  d-flex align-items-end justify-content-center mx-auto"
                            style={{
                                flex: 1,
                                aspectRatio: 1 / 1,
                                border: "4px solid #FFFDFE",
                                background: selectedImage ? "rgba(0,0,0,0.85)" : "#f4f4f4",
                                cursor: "pointer"
                            }}
                        >
                            <div
                                onClick={() => {
                                    if (isAnyActionInProgress) return;
                                    selectedImage ? setImageModal(true) : handleBoxClick();
                                }}
                                style={{
                                    width: selectedImage ? "100%" : "120%",
                                    height: selectedImage ? "100%" : "120%",
                                    backgroundImage: `url(${selectedImage || cachedImages.hintuser})`,
                                    backgroundSize: isAvatarImage ? "cover" : "cover",
                                    backgroundPosition: "center",
                                    backgroundRepeat: "no-repeat",
                                    opacity: isProcessing || isCheckingConnection ? 0.5 : 1,
                                    transition: "opacity 0.3s ease",
                                    cursor: isAnyActionInProgress ? "not-allowed" : "pointer"
                                }}
                            />

                            {(isProcessing || isCheckingConnection) && (
                                <div
                                    className="position-absolute top-0 start-0 d-flex justify-content-center align-items-center"
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        zIndex: 10
                                    }}
                                >
                                    <div
                                        className="custom-spinner position-absolute"
                                        style={{
                                            borderTopColor: "#FF207B"
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        {/* ================= */}

                        <div
                            className="d-flex justify-content-center align-items-start mt-3 mx-auto"
                            style={{
                                width: isSmallScreen ? '270px' : "330px",
                                height: isSmallScreen ? '270px' : "325px",
                            }}
                        >
                            <div ref={scrollContainerRef} className="overflow-y-auto d-flex flex-column gap-3 justify-content-center align-items-start rounded-4 p-2 position-relative"
                                style={{ width: "100%", height: "95%", background: "#fff", boxShadow: "0px 0.95px 2.84px 0px rgba(0,0,0,0.3), 0px 3.78px 7.56px 2.84px rgba(0,0,0,0.15)" }}>

                                <Row
                                    className="w-100 g-2 mx-auto h-100"
                                >
                                    <Col xs={4} >
                                        <div
                                            className="rounded-4 d-flex flex-column align-items-center justify-content-center"
                                            style={{
                                                width: "100%",
                                                aspectRatio: 1 / 1,
                                                background: "#FFEBEA",
                                                cursor: isAnyActionInProgress ? "not-allowed" : "pointer",
                                                pointerEvents: isAnyActionInProgress ? "none" : "auto",
                                                border: "2px dashed #FF827C"
                                            }}
                                            onClick={handleBoxClick2}
                                        >
                                            {isCheckingConnection2 ? (
                                                <div className="custom-spinner" style={{ borderTopColor: "#FF207B" }} />
                                            ) : (
                                                <>
                                                    <img src={cachedImages.annoycamera} alt="camera" width={35} />
                                                    <p className="m-0 pt-2 text-center"
                                                        style={{
                                                            lineHeight: "13px",
                                                            fontSize: "clamp(11px, 2vw, 14px)",
                                                            whiteSpace: "normal",        // 👈 allow wrapping
                                                            wordBreak: "normal",         // 👈 don't break words
                                                            overflowWrap: "break-word",  // 👈 only break if needed
                                                        }}
                                                    >
                                                        {t?.annoyCard?.addFace}
                                                    </p>
                                                </>
                                            )}
                                        </div>
                                    </Col>

                                    {loading
                                        ? Array.from({ length: AVATARS_PER_PAGE }).map((_, i) => (
                                            <Col xs={4} key={`skel-${i}`}>
                                                <div className="rounded-4" style={{
                                                    width: "100%", aspectRatio: "1/1",
                                                    background: "linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)",
                                                    backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite"
                                                }}
                                                />
                                            </Col>
                                        ))
                                        : imageUrls.map((url, i) => (
                                            <Col xs={4} key={url || i}>
                                                <div className="rounded-4 overflow-hidden position-relative"
                                                    style={{
                                                        width: "100%", aspectRatio: "1/1", cursor: "pointer",
                                                        background: "#FFEBEA",
                                                        border: selectedImage === url ? "2.5px solid #000" : "2.5px solid transparent"
                                                    }}
                                                    onClick={() => handleAvatarClick(url)}
                                                >
                                                    {/* Skeleton shown until image loads */}
                                                    {!imageLoadingStates[url] && (
                                                        <div className="position-absolute top-0 start-0 w-100 h-100 rounded-4 d-flex align-items-center justify-content-center"
                                                            style={{ background: "#f0f0f0", zIndex: 1 }}>
                                                            <img
                                                                src={placeholder}
                                                                alt="placeholder"
                                                                style={{ width: "50%", height: "50%", objectFit: "cover" }}
                                                            />
                                                        </div>
                                                    )}
                                                    <img src={url || placeholder}
                                                        alt="avatar"
                                                        onLoad={() => handleImageLoad(url)}
                                                        onError={(e) => { e.target.src = placeholder; handleImageLoad(url); }}
                                                        style={{
                                                            width: "100%", height: "100%", objectFit: "cover",
                                                            opacity: imageLoadingStates[url] ? 1 : 0, transition: "opacity 0.3s"
                                                        }}
                                                    />
                                                </div>
                                            </Col>
                                        ))
                                    }

                                </Row>
                            </div>
                        </div>

                        <div
                            className="d-flex justify-content-center align-items-center px-3 mx-auto mb-1"
                            style={{
                                // flex: 1,
                                width: "550px", maxWidth: "95vw",
                            }}
                        >
                            <Button
                                className='bg-black rounded-pill w-custom-50 border-0 mb-2 mx-auto GTMedium'
                                style={{
                                    padding: "14px 1px",
                                    height: "50px",
                                    width: "min(350px, 80vw)",
                                    maxWidth: "350px",
                                    opacity: (!isNextButtonEnabled()) ? 0.6 : 1,
                                    pointerEvents: isAnyActionInProgress ? 'none' : 'auto',
                                    cursor: (isAnyActionInProgress || !isNextButtonEnabled()) ? 'not-allowed' : 'pointer',
                                    transition: "opacity 0.3s ease"
                                }}
                                onClick={handleNextClick}
                                disabled={!isNextButtonEnabled() || isAnyActionInProgress}
                            >
                                {isCheckingConnectionNext ? (
                                    <div className="custom-spinner" style={{ borderTopColor: '#fff' }} />
                                ) : (
                                    <span
                                        style={{
                                            fontSize: "clamp(21px, 4.5vw, 27px)",
                                            lineHeight: 1,
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {t?.common?.next || "Next"}
                                    </span>
                                )}

                            </Button>
                        </div>
                    </Col>
                </Row>
            </div>


            {!isIOS && (
                <UploadModal
                    show={showModal}
                    onClose={() => setShowModal(false)}
                    onImageSelect={handleImageSelect}
                    onProcessing={(processing) => {
                        setIsProcessing(processing);
                    }}
                    onError={(errorMessage) => {
                        showSnackbar(errorMessage, "error");
                    }}
                    camera={cachedImages.camera}
                    gallery={cachedImages.gallery}
                    avatar={cachedImages.avatar}
                    t={t}
                    detect={true}
                />
            )}

            <RotatePhonePopup
                excludeWhen={[]}
            />

            <AnimatePresence mode="wait">
                {imageModal && (
                    <motion.div
                        className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{
                            zIndex: 99999,
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            backdropFilter: 'blur(5px)',
                        }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        onClick={() => setImageModal(false)}
                    >
                        <motion.div
                            className="rounded-4 p-3 text-center"
                            style={{
                                maxWidth: '350px',
                                width: '85%',
                                position: 'relative',
                            }}
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0, opacity: 0 }}
                            transition={{
                                duration: 0.4,
                                ease: [0.34, 1.56, 0.64, 1]
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setImageModal(false);
                            }}

                        >
                            <div
                                className="d-flex justify-content-center align-items-center position-relative"
                                onClick={() => setImageModal(false)}
                                style={{
                                    width: '100%',
                                    aspectRatio: '1/1',
                                    maxWidth: '280px',
                                    margin: '0 auto'
                                }}
                            >
                                <div
                                    className="position-relative"
                                    style={{
                                        width: '100%',
                                        height: '100%'
                                    }}
                                >
                                    <div
                                        className="rounded-circle border border-3 border-white overflow-hidden bg-white d-flex align-items-center justify-content-center"
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            backgroundImage: `url(${selectedImage})`,
                                            backgroundSize: "cover",
                                            backgroundPosition: "center",
                                            backgroundRepeat: "no-repeat",
                                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                        }}
                                    >
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Category3;